import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import SiteShell from "@/components/SiteShell";
import Packages from "@/components/Packages";
import LeadForm from "@/components/LeadForm";
import { trades, tradeList } from "@/data/trades";
import { videoAdsByTrade, displayCampaignsByTrade } from "@/data/reel";
import {
  Snowflake,
  Wrench,
  Zap,
  Home as HomeIcon,
  Leaf,
  Bug,
  AlertTriangle,
  Calendar,
  Quote,
  ArrowLeft,
  ArrowRight,
  Film,
} from "lucide-react";

const ICONS = { Snowflake, Wrench, Zap, Home: HomeIcon, Leaf, Bug };

const TradePage = () => {
  const { slug } = useParams();
  const trade = trades[slug];

  if (!trade) return <Navigate to="/home-services" replace />;

  const Icon = ICONS[trade.icon] || HomeIcon;
  const otherTrades = tradeList
    .filter((t) => t.slug !== trade.slug)
    .slice(0, 3);
  const tradeAds = videoAdsByTrade(trade.slug);
  const tradeCampaigns = displayCampaignsByTrade(trade.slug);
  const hasAnyAds = tradeAds.length > 0 || tradeCampaigns.length > 0;

  // Find the trade-specific colored word from heroHeadline by extracting a key word
  const headlineWords = trade.heroHeadline.split(" ");

  return (
    <SiteShell>
      {/* HERO */}
      <section
        className="relative bg-[var(--af-navy)] text-[var(--af-cream)] grid-bg overflow-hidden"
        data-testid="trade-hero"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-10 pb-24 sm:pt-14 sm:pb-28 relative z-10">
          <Link
            to="/home-services"
            className="inline-flex items-center gap-2 text-xs text-[var(--af-cream)]/55 hover:text-[var(--af-lime)] mb-10 transition-colors"
            data-testid="trade-back-link"
          >
            <ArrowLeft size={14} /> All Home Services
          </Link>

          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-8 fade-up">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-full bg-[var(--af-lime)]/15 border border-[var(--af-lime)]/40 flex items-center justify-center">
                  <Icon size={22} className="text-[var(--af-lime)]" />
                </div>
                <div className="eyebrow">{trade.heroEyebrow}</div>
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mb-7">
                {headlineWords.map((w, i) => {
                  // highlight key emotive words
                  const lower = w.toLowerCase().replace(/[.,!?]/g, "");
                  const highlights = [
                    "dies",
                    "picture",
                    "google",
                    "name",
                    "first",
                    "ev",
                    "panel",
                    "panel,",
                    "5pm",
                    "noon",
                    "storm",
                    "season",
                    "season.",
                    "marching",
                    "call",
                    "call.",
                    "mowing",
                    "starts",
                    "starts.",
                  ];
                  const isHl = highlights.includes(lower);
                  return (
                    <React.Fragment key={i}>
                      <span
                        className={
                          isHl ? "text-[var(--af-lime)]" : undefined
                        }
                      >
                        {w}
                      </span>{" "}
                    </React.Fragment>
                  );
                })}
              </h1>
              <p className="text-lg sm:text-xl text-[var(--af-cream)]/75 max-w-2xl mb-8 leading-relaxed">
                {trade.heroSub}
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="#packages"
                  className="btn-lime"
                  data-testid="trade-hero-cta-packages"
                >
                  See the 90-Day Packages <ArrowRight size={14} />
                </a>
                <a
                  href="#lead-form"
                  className="btn-ghost"
                  data-testid="trade-hero-cta-audit"
                >
                  Free {trade.shortName} Audit
                </a>
              </div>
            </div>

            <div className="lg:col-span-4">
              <div
                className="surface p-7 fade-up"
                style={{ animationDelay: ".15s" }}
              >
                <div className="eyebrow mb-3">Built for {trade.shortName}</div>
                <div className="font-display text-4xl leading-none mb-3">
                  90 days.
                </div>
                <div className="font-display text-4xl leading-none mb-3 text-[var(--af-lime)]">
                  411k ads.
                </div>
                <div className="font-display text-4xl leading-none">
                  One season.
                </div>
                <p className="text-sm mt-6 text-[var(--af-cream)]/70 leading-relaxed">
                  All packages under $10K. Three tiers. Cancel anytime after
                  90 days.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PAIN POINTS */}
      <section
        className="px-5 sm:px-8 py-24 max-w-7xl mx-auto"
        data-testid="trade-pain-section"
      >
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4">
              What We Hear From {trade.shortName} Owners
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl">
              You're not short on work.
              <br />
              You're short on the{" "}
              <span className="text-[var(--af-lime)]">right</span> work.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-4">
            {trade.painPoints.map((p, i) => (
              <div
                key={i}
                className="flex gap-4 items-start surface p-5"
                data-testid={`trade-pain-${i}`}
              >
                <AlertTriangle
                  size={20}
                  className="text-[var(--af-lime)] mt-0.5 flex-shrink-0"
                />
                <div className="text-[var(--af-cream)]/90 leading-relaxed">
                  {p}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DASHED DIVIDER */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="dashed-divider" />
      </div>

      {/* SEASONAL TRIGGERS */}
      <section
        className="px-5 sm:px-8 py-24"
        data-testid="trade-seasonal-section"
      >
        <div className="max-w-7xl mx-auto">
          <div className="mb-14 max-w-3xl">
            <div className="eyebrow mb-4">
              {trade.shortName} · Seasonal Triggers
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-4">
              Your ads run when your phone{" "}
              <span className="text-[var(--af-lime)]">should</span> be ringing.
            </h2>
            <p className="text-base sm:text-lg text-[var(--af-cream)]/75 max-w-2xl leading-relaxed">
              Weather, season, and behavior triggers, automatically, so you're
              first in front of the homeowner the moment they need you.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {trade.seasonalTriggers.map((s, i) => (
              <div
                key={i}
                className="surface p-6"
                data-testid={`trade-seasonal-${i}`}
              >
                <Calendar
                  size={22}
                  className="text-[var(--af-lime)] mb-4"
                />
                <div className="font-display text-xl mb-2">{s.season}</div>
                <div className="text-sm text-[var(--af-cream)]/70 leading-relaxed">
                  {s.trigger}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SAMPLE ADS — Navigational pointer only. Full gallery lives at
          /sample-ads to keep individual trade pages light. */}
      {hasAnyAds && (
        <section
          className="px-5 sm:px-8 py-24 border-t border-[var(--af-line)]"
          data-testid="trade-sample-ads"
        >
          <div className="max-w-7xl mx-auto">
            <div className="mb-10 max-w-3xl">
              <div className="flex items-center gap-3 mb-4">
                <Film size={18} className="text-[var(--af-lime)]" />
                <div className="eyebrow">
                  Sample Ads · {trade.shortName}
                </div>
              </div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-4">
                Real {trade.shortName} spots we&apos;ve{" "}
                <span className="text-[var(--af-lime)]">produced &amp; placed.</span>
              </h2>
              <p className="text-base sm:text-lg text-[var(--af-cream)]/75 max-w-2xl leading-relaxed">
                TV commercials, social media spots, vertical Reels, and
                interactive mobile display campaigns, built for real
                {" "}{trade.shortName.toLowerCase()} businesses in our market.
              </p>
            </div>
            <div
              className="surface p-7 sm:p-9 flex flex-col sm:flex-row sm:items-center justify-between gap-5"
              data-testid="trade-sample-ads-cta-card"
            >
              <div>
                <div className="font-eyebrow text-[10px] uppercase tracking-widest text-[var(--af-lime)] mb-2">
                  Open the gallery
                </div>
                <div className="font-display text-xl sm:text-2xl leading-tight">
                  See every {trade.shortName} ad we&apos;ve aired, filterable by
                  format.
                </div>
              </div>
              <Link
                to="/sample-ads"
                className="btn-lime whitespace-nowrap"
                data-testid="trade-sample-ads-see-all"
              >
                View sample ads <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* PACKAGES */}
      <Packages examples={trade.packageExamples} />

      {/* TESTIMONIAL */}
      {trade.testimonial && (
        <section
          className="grid-bg border-t border-b border-[var(--af-line)] px-5 sm:px-8 py-24"
          data-testid="trade-testimonial"
        >
          <div className="max-w-4xl mx-auto text-center">
            <Quote
              size={42}
              className="text-[var(--af-lime)] mx-auto mb-6"
            />
            <p className="font-display text-3xl sm:text-4xl mb-6 leading-tight">
              "{trade.testimonial.quote}"
            </p>
            <div className="eyebrow justify-center inline-flex">
              {trade.testimonial.author}
            </div>
          </div>
        </section>
      )}

      {/* LEAD FORM */}
      <section
        id="lead-form"
        className="px-5 sm:px-8 py-24"
        data-testid="trade-lead-form-section"
      >
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="eyebrow mb-4">{trade.shortName} · Start Here</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-6">
              Want a custom plan
              <br />
              for your{" "}
              <span className="text-[var(--af-lime)]">
                {trade.shortName.toLowerCase()}
              </span>{" "}
              business?
            </h2>
            <p className="text-base sm:text-lg text-[var(--af-cream)]/75 max-w-md mb-7 leading-relaxed">
              Tell us your service area and your top revenue goal for the next
              90 days. We'll come back with a tailored playbook, for free.
            </p>
            <ul className="space-y-2.5 text-sm text-[var(--af-cream)]/75">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                Free 20-minute strategy call
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                Custom targeting recommendations
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                Trade-specific creative direction
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                One-{trade.shortName.toLowerCase()}-per-market guarantee
              </li>
            </ul>
          </div>
          <LeadForm trade={trade.slug} tradeName={trade.name} />
        </div>
      </section>

      {/* RELATED TRADES */}
      <section
        className="px-5 sm:px-8 py-20 max-w-7xl mx-auto border-t border-[var(--af-line)]"
        data-testid="trade-related"
      >
        <div className="eyebrow mb-3">Other Home Services We Serve</div>
        <h3 className="font-display text-3xl sm:text-4xl mb-8">
          Got a friend in another trade?
        </h3>
        <div className="grid sm:grid-cols-3 gap-5">
          {otherTrades.map((t) => {
            const OI = ICONS[t.icon] || HomeIcon;
            return (
              <Link
                key={t.slug}
                to={`/home-services/${t.slug}`}
                className="surface p-5 flex items-center gap-4 group"
                data-testid={`trade-related-${t.slug}`}
              >
                <div className="w-10 h-10 rounded-full bg-[var(--af-lime)]/15 border border-[var(--af-lime)]/40 flex items-center justify-center">
                  <OI size={18} className="text-[var(--af-lime)]" />
                </div>
                <div>
                  <div className="font-display text-lg">{t.name}</div>
                  <div className="text-xs text-[var(--af-lime)] group-hover:underline underline-offset-4">
                    View playbook →
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </SiteShell>
  );
};

export default TradePage;
